import React from 'react';
import PropTypes from 'prop-types';
import {stringify} from 'query-string';
import {DragSource, DropTarget} from 'react-dnd';
import {getEmptyImage} from 'react-dnd-html5-backend';
import {Link} from 'react-router-dom';
import StoryboardChapterIcon from 'storyboard/components/storyboard-chapter-icon';
import StoryboardChapterLabel from 'storyboard/components/storyboard-chapter-label';
import StoryboardChapterMenu from 'storyboard/components/storyboard-chapter-menu';
import * as DragItemType from 'storyboard/constants/drag-item-type';
import {getClassNamesForEditable} from 'common/utils/style-util';
import Colors from 'common/constants/colors';
import './index.scss';

const chapterSource = {
  beginDrag(props) {
    return {
      id: props.id,
      originalIndex: props.findChapter(props.id).index,
    };
  },
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      const {id: droppedId, originalIndex} = monitor.getItem();
      props.moveChapter(droppedId, originalIndex);
    }
  },
};

const chapterTarget = {
  canDrop() {
    return false;
  },
  hover(props, monitor) {
    const {id: draggedId} = monitor.getItem();
    const {id: overId} = props;
    if (draggedId !== overId) {
      const {index: overIndex} = props.findChapter(overId);
      props.moveChapter(draggedId, overIndex);
    }
  },
};

const collectDrag = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging(),
});

const collectDrop = (connect, monitor) => ({
  canDrop: monitor.canDrop(),
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
});

class StoryboardNavButton extends React.Component {
  static defaultProps = {
    active: false,
    chapter: {},
    fontSize: '1.5vmin',
    margins: {},
    openModal: () => {},
    textColor: Colors.white,
    width: 0,
  };

  static propTypes = {
    active: PropTypes.bool,
    chapter: PropTypes.object,
    connectDragPreview: PropTypes.func.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    connectDropTarget: PropTypes.func.isRequired,
    data: PropTypes.object.isRequired,
    enums: PropTypes.object.isRequired,
    findChapter: PropTypes.func.isRequired,
    fontSize: PropTypes.string,
    id: PropTypes.any.isRequired,
    isDragging: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    margins: PropTypes.objectOf(PropTypes.number),
    moveChapter: PropTypes.func.isRequired,
    openModal: PropTypes.func,
    textColor: PropTypes.string,
    width: PropTypes.number,
  };

  componentDidMount() {
    this.props.connectDragPreview(getEmptyImage(), {
      captureDraggingState: true,
    });
    this.setTitle(this.props.active, this.props.chapter, this.props.data);
  }

  componentDidUpdate() {
    this.setTitle(this.props.active, this.props.chapter, this.props.data);
  }

  getBackgroundColor(active) {
    return active ? 'rgba(255, 255, 255, 0.15)' : null;
  }

  setTitle(active, chapter, data) {
    if (active) {
      document.title = `${data.displayName} - ${chapter.title}`;
    }
  }

  render() {
    const classNames = getClassNamesForEditable(
      'StoryboardNavButton',
      this.props.data.isEditable,
    );
    const {connectDragSource, connectDropTarget, isDragging} = this.props;
    const backgroundColor = this.getBackgroundColor(this.props.active);
    const Component = (
      <div
        className={classNames}
        style={{
          backgroundColor,
          marginBottom: this.props.margins.bottom,
          marginLeft: this.props.margins.left,
          marginRight: this.props.margins.right,
          marginTop: this.props.margins.top,
          maxWidth: this.props.width,
          opacity: isDragging ? 0 : 1,
        }}>
        <Link
          to={{
            pathname: this.props.location.pathname,
            search: stringify({
              chapterId: this.props.chapter.id,
            }),
          }}>
          <StoryboardChapterIcon
            active={this.props.active}
            type={this.props.chapter.icon.id}
          />
          <StoryboardChapterLabel
            color={this.props.textColor}
            fontSize={this.props.fontSize}
            text={this.props.chapter.title}
          />
        </Link>

        {this.props.data.isEditable ? (
          <StoryboardChapterMenu
            arrowFill={this.props.textColor}
            chapter={this.props.chapter}
            data={this.props.data}
            enums={this.props.enums}
            openModal={this.props.openModal}
          />
        ) : null}
      </div>
    );

    if (this.props.data.isEditable) {
      return connectDragSource(connectDropTarget(Component));
    }
    return Component;
  }
}

// Intermediate drag source component
const ComponentDragSource = DragSource(
  DragItemType.CHAPTER,
  chapterSource,
  collectDrag,
)(StoryboardNavButton);

// Export drag target
export default DropTarget(
  DragItemType.CHAPTER,
  chapterTarget,
  collectDrop,
)(ComponentDragSource);
